import { COURSE_DURATIONS } from '@/config/courseDurations';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import VideoPlayer from '@/components/VideoPlayer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link } from 'react-router-dom';
import { OcSchoolsFinder } from '@/components/OcSchoolsFinder';
import { Helmet } from 'react-helmet-async';
import { useCoursePricing } from '@/hooks/useCoursePricing';
import { Target, Users, Clock, BookOpen, CheckCircle, ArrowRight, Star, Award, Brain, Calculator, FileText, Lightbulb, Trophy, PlayCircle, Image, Upload, DollarSign, Calendar, Phone, Mail, MessageCircle } from 'lucide-react';
import { BreadcrumbSchema, generateProgramBreadcrumbs, OCProgramVideoSchema, ProgramSpeakableSchema } from '@/components/seo';
import { buildCourseSchema, offersFromPricing, TUTORING_PROVIDER } from '@/data/courses';
const OcProgram = () => {
  // Fetch OC pricing data from Supabase
  const { pricing: ocptcPricing } = useCoursePricing('NSW', 'ocptc');
  const { pricing: ocPricing } = useCoursePricing('NSW', 'oc');
  
  // Helper function to get pricing values
  const getWeeklyPrice = () => {
    if (ocptcPricing.length > 0) {
      return ocptcPricing[0].price_per_session;
    }
    return 77; // fallback
  };
  
  const getTermPrice = () => {
    if (ocptcPricing.length > 0) {
      return ocptcPricing[0].price_per_session * ocptcPricing[0].total_sessions;
    }
    return 770; // fallback
  };
  
  const getBooksCost = () => {
    if (ocPricing.length > 0) {
      return ocPricing[0].books_cost;
    }
    return 77; // fallback
  };
  const ocTestAreas = [{
    name: 'Reading Test',
    icon: BookOpen,
    description: 'The Reading Test questions assess various reading skills through a diverse range of texts.',
    details: 'The texts include different genres such as: non-fiction, fiction, poetry, magazine articles, reports.',
    questions: '13',
    questionsNote: '',
    time: '30 minutes',
    weighting: '33.3%'
  }, {
    name: 'Mathematical Reasoning Test',
    icon: Calculator,
    description: 'The Mathematical Reasoning Test assesses the student\'s ability to use mathematical understanding and knowledge to solve problems from a range of math topics.',
    details: 'Calculators are not used in the Mathematical Reasoning Test or any other test section.',
    questions: '35',
    questionsNote: '',
    time: '40 minutes',
    weighting: '33.3%'
  }, {
    name: 'Thinking Skills Test',
    icon: Brain,
    description: 'The Thinking Skills Test evaluates the student\'s critical thinking and problem-solving abilities through various question types.',
    details: 'No previous knowledge is required for this test.',
    questions: '30',
    questionsNote: '',
    time: '30 minutes',
    weighting: '33.3%'
  }];
  const schoololaAdvantages = ['Expert NSW curriculum teachers with OC specialization', 'Small class sizes (8-10 students) for personalized attention', 'Comprehensive practice tests and mock examinations', 'Strategic time management and exam techniques', 'Regular progress monitoring and feedback', 'Stress management and confidence building', 'Access to exclusive OC practice materials', 'Technology-focused learning with mobile apps', 'Free homework support and video resources'];
  const whyChooseSchoolola = [{
    title: 'Expert Teachers',
    description: 'Qualified NSW curriculum teachers specialising in OC preparation methodologies',
    icon: Users
  }, {
    title: 'Comprehensive Approach',
    description: 'Complete coverage of all OC test areas with strategic exam preparation',
    icon: Target
  }];
  const blogTopics = [{
    title: 'How to Use the NSW OC Score Calculator: A Parent\'s Guide',
    link: '/blog/how-to-use-oc-calculator',
    featured: true
  }, {
    title: 'Complete Guide to OC Test Preparation 2024',
    link: '/blog/oc-test-preparation-2024'
  }, {
    title: 'Understanding OC Test Mathematics Section',
    link: '/blog/oc-mathematics-section'
  }, {
    title: 'How to Improve Reading Comprehension for OC',
    link: '/blog/oc-reading-comprehension'
  }, {
    title: 'OC Test General Ability Questions Explained',
    link: '/blog/oc-general-ability'
  }, {
    title: 'Time Management Strategies for OC Success',
    link: '/blog/oc-time-management'
  }, {
    title: 'Common OC Test Mistakes to Avoid',
    link: '/blog/oc-common-mistakes'
  }, {
    title: 'Building Confidence for OC Test Day',
    link: '/blog/oc-confidence-building'
  }, {
    title: 'OC vs Selective: Which Path is Right?',
    link: '/blog/oc-vs-selective'
  }, {
    title: 'Parent Guide to Supporting OC Preparation',
    link: '/blog/oc-parent-guide'
  }, {
    title: 'Mock Test Strategies for OC Success',
    link: '/blog/oc-mock-test-strategies'
  }];
  return <div className="min-h-screen bg-white">
      <Helmet>
        <title>NSW OC Test Preparation | Opportunity Class Tutoring | Schoolola</title>
        <meta name="description" content="Expert NSW OC test preparation. Specialised Opportunity Class tutoring for Years 3 & 4. Small classes. Book free assessment today." />
        <meta name="keywords" content="OC test preparation, Opportunity Class tutoring, NSW OC coaching, OC test practice, Opportunity Class placement, OC exam preparation, NSW selective tutoring" />
        <meta property="og:title" content="NSW OC Test Preparation | Opportunity Class Tutoring | Schoolola" />
        <meta property="og:description" content="Expert NSW OC test preparation. Specialised Opportunity Class tutoring for Years 3 & 4." />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify(buildCourseSchema('oc-tmrw', { workload: COURSE_DURATIONS.ocTmrw.iso, offers: offersFromPricing(ocPricing), provider: TUTORING_PROVIDER }))}</script>
      </Helmet>
      
      <BreadcrumbSchema items={generateProgramBreadcrumbs("OC TMRW™ Course", "oc-preparation")} />
      <OCProgramVideoSchema />
      <ProgramSpeakableSchema programName="NSW OC Test Preparation" slug="oc-preparation" />
      
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 via-white to-accent-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center space-y-8">
            <Badge className="bg-accent text-white px-6 py-3 text-lg rounded-2xl">
              NSW Opportunity Class Programme
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-heading font-bold text-gray-900">
              Master the OC Test with NSW's Leading Experts
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
              Comprehensive Opportunity Class preparation designed by NSW education specialists. 
              Our proven methodology has helped developed our proprietary TMRW course that has helped thousands of students secure their place in Opportunity Classes across NSW.
            </p>
            
            {/* Video Section */}
            <div className="max-w-2xl mx-auto mt-8">
              <VideoPlayer src="/oc-program-video.mp4" className="shadow-lg" autoplay={true} controls={false} muted={true} loop={true} />
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book-assessment">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-lg px-8 rounded-2xl">
                  Book Free Assessment
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/nsw-oc-calculator">
                <Button variant="outline" size="lg" className="text-lg px-8 rounded-2xl border-accent text-accent hover:bg-accent hover:text-white">
                  <Calculator className="mr-2 h-5 w-5" />
                  Free OC Score Calculator
                </Button>
              </Link>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              <Link to="/contact" className="hover:text-primary underline">Enquire about smaller groups</Link>
            </p>
          </div>
        </div>
      </section>

      {/* What is the OC Test Section - Modern Design */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-4 mb-12">
              <Badge className="bg-primary/10 text-primary px-4 py-2 rounded-2xl">
                NSW OC Test Overview
              </Badge>
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-gray-900">
                Understanding the <span className="text-primary">Opportunity Class Test</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                A selective entrance examination for academically gifted students in Years 3 and 4, designed to identify students who would benefit from accelerated learning opportunities
              </p>
              <div className="flex justify-center mt-4">
                <Link to="/nsw-oc-calculator" className="inline-flex items-center text-primary hover:text-primary/80 font-medium transition-colors">
                  <Calculator className="mr-2 h-4 w-4" />
                  Check your child's chances with our free OC Score Calculator
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </div>
            </div>

            {/* Test Areas - Desktop Grid, Mobile Carousel */}
            <div className="hidden md:grid md:grid-cols-1 lg:grid-cols-3 gap-6">
              {ocTestAreas.map((area, index) => <Card key={index} className="relative border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
                  <CardContent className="p-6">
                    {/* Header */}
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="h-12 w-12 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                        <area.icon className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading font-bold text-gray-900 text-lg leading-tight">
                          {area.name}
                        </h3>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 text-sm leading-relaxed mb-3">
                      {area.description}
                    </p>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      {area.details}
                    </p>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="bg-primary/5 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-primary">{area.questions}</div>
                        <div className="text-xs text-gray-600">Questions</div>
                      </div>
                      <div className="bg-accent/5 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-accent">{area.time.split(' ')[0]}</div>
                        <div className="text-xs text-gray-600">Minutes</div>
                      </div>
                    </div>

                    {/* Additional Info */}
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Weight:</span>
                        <span className="font-medium text-gray-800">{area.weighting}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>)}
            </div>

            {/* Mobile Carousel */}
            <div className="md:hidden">
              <Carousel className="w-full" plugins={[Autoplay({
              delay: 3000
            }) as any]} opts={{
              align: "start",
              loop: true
            }}>
                <CarouselContent className="-ml-4">
                  {ocTestAreas.map((area, index) => <CarouselItem key={index} className="pl-4">
                      <Card className="relative border-0 shadow-lg">
                        <CardContent className="p-6">
                          {/* Header */}
                          <div className="flex items-center space-x-3 mb-4">
                            <div className="h-12 w-12 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center">
                              <area.icon className="h-6 w-6 text-white" />
                            </div>
                            <div className="flex-1">
                              <h3 className="font-heading font-bold text-gray-900 text-lg leading-tight">
                                {area.name}
                              </h3>
                            </div>
                          </div>

                          {/* Description */}
                          <p className="text-gray-600 text-sm leading-relaxed mb-3">
                            {area.description}
                          </p>
                          <p className="text-gray-600 text-sm leading-relaxed mb-4">
                            {area.details}
                          </p>

                          {/* Stats Grid */}
                          <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="bg-primary/5 rounded-lg p-3 text-center">
                              <div className="text-2xl font-bold text-primary">{area.questions}</div>
                              <div className="text-xs text-gray-600">Questions</div>
                            </div>
                            <div className="bg-accent/5 rounded-lg p-3 text-center">
                              <div className="text-2xl font-bold text-accent">{area.time.split(' ')[0]}</div>
                              <div className="text-xs text-gray-600">Minutes</div>
                            </div>
                          </div>

                          {/* Additional Info */}
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between items-center">
                              <span className="text-gray-500">Weight:</span>
                              <span className="font-medium text-gray-800">{area.weighting}</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </CarouselItem>)}
                </CarouselContent>
              </Carousel>
            </div>

            {/* Bottom Summary Bar */}
            <div className="mt-12 bg-white rounded-2xl shadow-lg p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center mb-6">
                <div className="space-y-1">
                  <div className="text-2xl font-bold text-primary">78</div>
                  <div className="text-sm text-gray-600">Total Questions</div>
                </div>
                <div className="space-y-1">
                  <div className="text-2xl font-bold text-accent">1.67</div>
                  <div className="text-sm text-gray-600">Hours</div>
                </div>
                <div className="space-y-1">
                  <div className="text-2xl font-bold text-primary">3</div>
                  <div className="text-sm text-gray-600">Test Areas</div>
                </div>
                <div className="space-y-1">
                  <div className="text-2xl font-bold text-accent">33.3%</div>
                  <div className="text-sm text-gray-600">Each Section</div>
                </div>
              </div>
              
              {/* Calculator CTA */}
              <div className="border-t pt-6 text-center">
                <p className="text-gray-600 mb-4">Wondering if your child can get into their dream OC school?</p>
                <Link to="/nsw-oc-calculator">
                  <Button className="bg-accent hover:bg-accent/90 text-white">
                    <Calculator className="mr-2 h-4 w-4" />
                    Try Our Free OC Score Calculator
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Schoolola's Premium Courses */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-4 mb-16">
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-gray-900">
                Schoolola's Premium OC Courses
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Choose from flexible payment options - pay per term or weekly membership
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* OC TMRW Course */}
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-shadow">
                <CardHeader className="bg-gradient-to-r from-primary to-primary-600 text-white rounded-t-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <Badge className="bg-accent text-white mb-2">BESTSELLER</Badge>
                      <CardTitle className="text-2xl font-heading">
                        OC TMRW™
                      </CardTitle>
                      <p className="text-primary-100 mt-2">The Tomorrow Method for OC Success</p>
                    </div>
                    <Brain className="h-12 w-12 text-primary-100" />
                  </div>
                </CardHeader>
                <CardContent className="p-8 space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <Clock className="h-5 w-5 text-primary" />
                      <span className="font-semibold">{COURSE_DURATIONS.ocTmrw.page}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Calendar className="h-5 w-5 text-primary" />
                      <span className="font-semibold">10 weeks per term</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Users className="h-5 w-5 text-primary" />
                      <span className="font-semibold">8-10 students per class</span>
                    </div>
                  </div>
                  
                  <div className="border-t pt-6">
                    <h4 className="font-semibold text-gray-900 mb-4">Flexible Payment Options:</h4>
                    <div className="bg-primary-50 rounded-lg p-4 mb-4">
                      <h5 className="font-semibold text-primary mb-2">Option 1: Term Payment</h5>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span>Term Rate (incl. GST)</span>
                          <span className="font-bold text-primary text-lg">${getTermPrice()}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span>Course Books (extra)</span>
                          <span className="font-semibold text-accent">${getBooksCost()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-accent-50 rounded-lg p-4">
                      <h5 className="font-semibold text-accent mb-2">Option 2: Weekly Membership</h5>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span>Weekly Rate (incl. GST)</span>
                          <span className="font-bold text-accent text-lg">${getWeeklyPrice()}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span>Course Books (extra)</span>
                          <span className="font-semibold text-accent">${getBooksCost()}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-4">
                    <h5 className="font-semibold text-gray-900 mb-2">What's Included:</h5>
                    <ul className="text-sm space-y-1 text-gray-700">
                      <li>• Comprehensive OC test preparation</li>
                      <li>• Weekly practice tests and assessments</li>
                      <li>• Expert teacher guidance and feedback</li>
                      <li>• Strategic exam techniques</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* OCPTC Course */}
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-shadow">
                <CardHeader className="bg-gradient-to-r from-accent to-orange-500 text-white rounded-t-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <Badge className="bg-primary text-white mb-2">TEST PACK COURSE</Badge>
                      <CardTitle className="text-2xl font-heading">
                        OCPTC™
                      </CardTitle>
                      <p className="text-orange-100 mt-2">OC Practice Test Course</p>
                    </div>
                    <FileText className="h-12 w-12 text-orange-100" />
                  </div>
                </CardHeader>
                <CardContent className="p-8 space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <Clock className="h-5 w-5 text-accent" />
                      <span className="font-semibold">2.5 hours one session per week</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Calendar className="h-5 w-5 text-accent" />
                      <span className="font-semibold">10 weeks per term</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Users className="h-5 w-5 text-accent" />
                      <span className="font-semibold">8-10 students per class</span>
                    </div>
                  </div>
                  
                  <div className="border-t pt-6">
                    <h4 className="font-semibold text-gray-900 mb-4">Flexible Payment Options:</h4>
                    <div className="bg-accent-50 rounded-lg p-4 mb-4">
                      <h5 className="font-semibold text-accent mb-2">Option 1: Term Payment</h5>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span>Term Rate (incl. GST)</span>
                          <span className="font-bold text-accent text-lg">${getTermPrice()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-primary-50 rounded-lg p-4">
                      <h5 className="font-semibold text-primary mb-2">Option 2: Weekly Membership</h5>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span>Weekly Rate (incl. GST)</span>
                          <span className="font-bold text-primary text-lg">${getWeeklyPrice()}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-orange-50 rounded-lg p-4">
                    <h5 className="font-semibold text-gray-900 mb-2">What's Included:</h5>
                    <ul className="text-sm space-y-1 text-gray-700">
                      <li>• Extensive practice test library</li>
                      <li>• Detailed performance analysis</li>
                      <li>• Time management strategies</li>
                      <li>• Exam technique refinement</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="text-center mt-12 p-6 bg-blue-50 rounded-2xl">
              <h3 className="text-xl font-heading font-bold text-gray-900 mb-2">
                Want Smaller Group Classes?
              </h3>
              <p className="text-gray-600 mb-4">
                Contact your local Schoolola centre to enquire about smaller group options and availability
              </p>
              <Link to="/contact">
                <Button className="bg-primary hover:bg-primary/90">
                  Enquire at Your Local Centre
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Topics Section - Carousel */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-4 mb-12">
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-gray-900">
                OC Test Preparation Resources
              </h2>
              <p className="text-xl text-gray-600">
                Expert insights and strategies for OC success
              </p>
            </div>
            
            <Carousel className="w-full" plugins={[Autoplay({
            delay: 4000
          }) as any]} opts={{
            align: "start",
            loop: true
          }}>
              <CarouselContent className="-ml-2 md:-ml-4">
                {blogTopics.map((topic, index) => <CarouselItem key={index} className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/3 h-60 flex items-center">
                     <Link to={topic.link} className="group w-full">
                       <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300 h-48">
                         <CardContent className="p-6 h-full flex flex-col">
                          <div className="flex items-start space-x-3 flex-grow">
                            <div className="h-12 w-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                              <BookOpen className="h-6 w-6 text-primary" />
                            </div>
                            <div className="flex-grow">
                              <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors leading-tight mb-3">
                                {topic.title}
                              </h3>
                              <p className="text-sm text-gray-600">
                                Expert tips and strategies for OC test success
                              </p>
                            </div>
                          </div>
                          
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <div className="flex items-center text-primary text-sm font-medium group-hover:text-primary/80 transition-colors">
                              Read Article
                              <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  </CarouselItem>)}
              </CarouselContent>
              
              {/* Navigation Arrows */}
              <CarouselPrevious className="hidden md:flex -left-12 lg:-left-16" />
              <CarouselNext className="hidden md:flex -right-12 lg:-right-16" />
            </Carousel>
          </div>
        </div>
      </section>


      {/* Why Choose Schoolola */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-4 mb-16">
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-gray-900">
                Why Schoolola is NSW's Leading OC Specialist
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our proven track record and expert methodology make us the trusted choice for OC preparation
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-16">
              {whyChooseSchoolola.map((reason, index) => <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow text-center">
                  <CardContent className="p-8 space-y-4">
                    <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                      <reason.icon className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900">
                      {reason.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {reason.description}
                    </p>
                  </CardContent>
                </Card>)}
            </div>

            <Card className="border-0 shadow-lg bg-gradient-to-r from-primary-50 to-accent-50">
              <CardContent className="p-8">
                <h3 className="text-2xl font-heading font-bold text-gray-900 mb-6 text-center">
                  The Schoolola Advantage
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {schoololaAdvantages.map((advantage, index) => <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{advantage}</span>
                    </div>)}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Success Statistics */}
      <section className="py-20 bg-gradient-to-r from-primary to-primary-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white">
              Proven OC Success Results
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="text-center">
                <div className="text-5xl font-bold text-accent mb-2">3+</div>
                <div className="text-primary-100 font-medium">Years of Excellence</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-accent mb-2">500+</div>
                <div className="text-primary-100 font-medium">Successful Students</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OC Schools Finder */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <OcSchoolsFinder />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto space-y-8">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-gray-900">
              Start Your Child's OC Journey Today
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Join thousands of successful students who achieved their OC dreams with Schoolola's expert guidance
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book-assessment">
                <Button size="lg" className="bg-accent hover:bg-accent/90 text-lg px-8 rounded-2xl">
                  Book Your Free Assessment
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" size="lg" className="text-lg px-8 rounded-2xl">
                  Contact Your Local Centre
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>;
};
export default OcProgram;